import { motion } from 'framer-motion'
import { useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { useProductsOpenApi } from 'src/api/db/apiProductsOpen'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { ReorderButton } from 'src/pages/_myAccount/Requests/components/ReorderButton'

import { ProductImage } from '../../ProductImage'
import { Column, Row } from '../../Semantic/all'
import { Skeleton } from '../../ui/skeleton'
import { ProductPreview } from './ProductPreview'
import { ProductInfo } from './components/ProductInfo'
// import { CircleProgress } from './components/CircleProgress'

export const ProductCard = ({
  product_item,
  isChat = false,
  showAddToQuote = true,
  showReorder = false,
  addToCurrentQuoteId,
  favouritesOrgId,
}: {
  product_item?: {
    product_id?: number
    product_variant_color_id?: number
    relevance_score?: number
  }
  numColumns?: number
  isChat?: boolean
  showAddToQuote?: boolean
  showReorder?: boolean
  addToCurrentQuoteId?: number
  favouritesOrgId?: string | null
}) => {
  const [open, setOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const { t } = useLocalization()
  const navigate = useNavigate()
  const location = useLocation()
  const currency = useOrganizationCurrency()

  const { productsByIdsOpen: products } = useProductsOpenApi({
    product_id: product_item?.product_id!,
  })
  const product = products?.data?.[0] as any

  const productName = t(product?.item_name_translations)
  const product_description = t(product?.item_description_translations)

  const price = product?.price
  const moq = product?.moq || 1
  const product_variant = product?.product_variant?.[0]
  const product_variant_colors =
    product_variant?.product_variant_color as ProductVariantColor_[]
  const chosen_product_variant_color =
    product_variant_colors?.find(
      (product_variant_color) =>
        product_variant_color.id === product_item?.product_variant_color_id
    ) || product_variant_colors?.[0]
  const image_url = chosen_product_variant_color?.product_image?.sort(
    (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
  )[0]?.image_url

  const { quoteItems } = useQuoteItemApi({ quote_id: addToCurrentQuoteId })
  const inOrderQuoteItem = quoteItems.data?.find(
    (item) => item.product_id === product_item?.product_id
  )

  const isMobile = window.innerWidth < 640
  const isLoading = !product || !imageLoaded

  const openProduct = () => {
    if (!product?.id) return
    const isFromShareOrder = location.pathname.includes('/share-order')
    const productPath = isFromShareOrder
      ? `${paths.product}/${product.id}?noHeader=true`
      : `${paths.product}/${product.id}`
    !isMobile && isChat ? setOpen(true) : navigate(productPath)
  }

  return (
    <motion.div ref={cardRef} className="w-full">
      <Column className="card-new catalog-product-card overflow-hidden gap-0 p-0 transition-transform duration-200 hover:-translate-y-1">
        <Column
          className="relative items-center overflow-hidden bg-muted/40"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="relative w-full min-h-[200px]">
            {/* Skeleton loading */}
            {isLoading && (
              <div className="absolute inset-0 z-10">
                <Skeleton className="w-full h-full rounded-none" />
              </div>
            )}

            {/* Real product - fades in when loaded */}
            <motion.div
              className="w-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: isLoading ? 0 : 1 }}
              transition={{ duration: 0.3 }}
            >
              <ProductImage
                image_url={image_url}
                onClick={openProduct}
                onImageLoad={() => setImageLoaded(true)}
                klayder_recommends={product?.klayder_recommends}
                product_id={product?.id}
                containerClassName="rounded-none"
                imageClassName="object-contain max-h-[300px] rounded-none"
              />
            </motion.div>

            {/* Hover buttons overlay - only shows when product is loaded */}
            {!isLoading && (
              <ProductInfo
                isChat={isChat}
                setOpen={setOpen}
                isHovered={isHovered}
                product={product}
                product_variant_color_id={
                  product_item?.product_variant_color_id ||
                  product?.product_variant?.[0]?.product_variant_color?.[0]?.id
                }
                showAddToQuote={showAddToQuote}
                showPreviewButton={
                  !isChat && location.pathname !== paths.allProducts
                }
                addToCurrentQuoteId={addToCurrentQuoteId}
                inOrderQuoteItemId={inOrderQuoteItem?.id}
              />
            )}
          </div>
        </Column>

        {!isLoading && (
          <Column className="gap-1.5 p-3 pt-2.5">
            <button
              type="button"
              className="line-clamp-2 cursor-pointer text-left transition-colors hover:text-muted-foreground"
              onClick={openProduct}
            >
              <h4>{productName}</h4>
            </button>

            <p
              className="text-xs text-muted-foreground line-clamp-2"
              dangerouslySetInnerHTML={{ __html: product_description }}
            />

            <Row className="items-center gap-2">
              <span className="text-sm text-foreground">
                {price?.toLocaleString() || '-'} {currency}
              </span>

              {/* {product_item?.relevance_score && (
                <CircleProgress score={product_item.relevance_score} />
              )} */}
            </Row>

            {showReorder && productName && (
              <ReorderButton
                className="mt-2"
                message={t(
                  `Reorder the ${productName} from our previous order`,
                  `Beställ om ${productName} från vår tidigare order`
                )}
                requestName={`${t('Reorder', 'Ombeställning')}: ${productName}`}
              />
            )}
          </Column>
        )}
      </Column>

      <ProductPreview
        open={open}
        setOpen={setOpen}
        chosen_product_variant_color={chosen_product_variant_color}
        product_variant_colors={product_variant_colors}
        product_name={productName}
        product_description={product_description}
        moq={moq}
        price={price || 0}
        article_number={product?.article_number || ''}
        product_id={product?.id}
        klayder_recommends={product?.klayder_recommends}
        klayder_recommendation_text={product?.klayder_recommendation_text}
        related_product_ids={product?.related_product_ids}
        product_categories={product?.category_product}
        addToCurrentQuoteId={addToCurrentQuoteId}
        favouritesOrgId={favouritesOrgId}
        showAddToQuote={showAddToQuote}
      />
    </motion.div>
  )
}
